import React, { useEffect, useState } from 'react';

import { KbaAnswers, kbaApi, KbaQuestion } from 'api/KbaApi';
import BlurPopup from 'components/BlurPopup';
import Loader from 'components/Loader/index';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { getApplicationInfoForDashboardThunk } from 'thunks/getApplicationInfoForDashboard';

import FinalStep from './FinalStep';
import InitialStep from './InitialStep';
import styles from './KbaPopup.module.scss';
import SurveyStep from './SurveyStep';
import { KBA_EXPIRE_SEC } from './SurveyStep/surveyStep.constants';

enum KbaSteps {
  Initial = 'Initial',
  Survey = 'Survey',
  Final = 'Final',
}

interface KbaPopupProps {
  kbaBorrowerRequired: boolean;
  kbaCoborrowerRequired: boolean;
}

const KbaPopup = ({ kbaBorrowerRequired, kbaCoborrowerRequired }: KbaPopupProps) => {
  const [open, setOpen] = useState(kbaBorrowerRequired || kbaCoborrowerRequired);
  const [questions, setQuestion] = useState<KbaQuestion[]>([]);
  const [startTimestamp, setStartTimestamp] = useState<number>(0);
  const [step, setStep] = useState<KbaSteps>(KbaSteps.Initial);
  const [loading, setLoading] = useState<boolean>(false);
  const { applicationId, variables } = useAppSelector((state) => state.application);
  const { borrowerId } = useAppSelector((state) => state.borrower);

  const borrowerType = variables?.borrower_kba_required && !variables.borrower_kba_status ? 'borrower' : 'coborrower';
  const borrowerName = borrowerType === 'borrower' ? variables?.borrower_first_name : variables?.coborrower_first_name;

  const dispatch = useAppDispatch();
  const onClose = async () => {
    setOpen(false);
    setStep(KbaSteps.Initial);
    await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
  };

  const onStart = async () => {
    if (applicationId) {
      setLoading(true);

      const { questions: questionsResponse, timestamp } = await kbaApi.startKba({
        applicationId: applicationId,
        borrowerType: borrowerType,
      });

      setQuestion(questionsResponse);
      setStep(KbaSteps.Survey);
      setStartTimestamp(timestamp || 0);
      setLoading(false);
    }
  };

  const onExpire = async () => {
    if (applicationId) {
      setLoading(true);

      await kbaApi.verifyKbaAnswers({
        applicationId: applicationId,
        borrowerType: borrowerType,
        answers: [],
      });

      setStep(KbaSteps.Final);
      setLoading(false);
    }
  };

  const onConfirm = async (answers: string[]) => {
    if (applicationId) {
      setLoading(true);

      const answersWithType: KbaAnswers[] = questions.map((question, index) => {
        return {
          text: answers[index],
          questionType: question.questionType,
        };
      });

      await kbaApi.verifyKbaAnswers({
        applicationId: applicationId,
        borrowerType: borrowerType,
        answers: answersWithType,
      });

      setStep(KbaSteps.Final);
      setLoading(false);
    }
  };
  const checkStartingTimestamp = async () => {
    setLoading(true);

    if (applicationId) {
      const { isStart, timestamp } = await kbaApi.getStartingTimestamp({
        applicationId: applicationId,
        borrowerType: borrowerType,
      });

      if (isStart && timestamp) {
        const elapsedSecs = (Date.now() - timestamp) / 1000;

        if (elapsedSecs < KBA_EXPIRE_SEC) {
          await onStart();
        } else {
          await onExpire();
        }
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    const isOpen = kbaBorrowerRequired || kbaCoborrowerRequired;

    if (isOpen && step === KbaSteps.Initial) {
      checkStartingTimestamp();
    }

    setOpen(isOpen);
  }, [kbaBorrowerRequired, kbaCoborrowerRequired]);

  const renderKbaStepWidget = () => {
    if (loading) {
      return <Loader customContainerClassname={styles.loaderContainer} />;
    }

    if (step === KbaSteps.Initial) {
      return <InitialStep onStart={onStart} borrowerName={borrowerName} />;
    }

    if (step === KbaSteps.Survey) {
      return (
        <SurveyStep questions={questions} onConfirm={onConfirm} onExpire={onExpire} startTimestamp={startTimestamp} />
      );
    }

    if (step === KbaSteps.Final) {
      return <FinalStep />;
    }
  };

  return (
    <BlurPopup
      id="kba_popup"
      classes={{
        paper: styles.paper,
        container: styles.container,
      }}
      onClose={onClose}
      open={open}
      scroll="body"
      isCloseButtonDisplayed={step === KbaSteps.Final}
      blurDeep={2}
    >
      {renderKbaStepWidget()}
    </BlurPopup>
  );
};

export default KbaPopup;
